import type {EditableProps} from '@chakra-ui/react';
import {Editable} from '@chakra-ui/react';
import {zodResolver} from '@hookform/resolvers/zod';
import type {DefaultValues, FieldValues, SubmitHandler} from 'react-hook-form';
import {FormProvider, useForm} from 'react-hook-form';
import {useMemo} from 'react';
import type {ZodTypeAny} from 'zod';

import {useEditableFieldsStyleConfig} from '../../theme-config';

import {EditableRoot} from './EditableRoot';

export type EditableFieldsProps<TFieldValues extends FieldValues = FieldValues> = {
  defaultValues?: DefaultValues<TFieldValues>;
  onSubmit?: SubmitHandler<TFieldValues>;
  schema?: ZodTypeAny;
  closeOnBlur?: boolean;
  closeOnEsc?: boolean;
  closeOnClickOutside?: boolean;
} & Omit<EditableProps, 'onSubmit' | 'value' | 'defaultValue'>;

export const EditableFields = <TValues extends FieldValues = FieldValues>(props: EditableFieldsProps<TValues>) => {
  const {
    onSubmit,
    closeOnBlur = true,
    closeOnClickOutside = true,
    closeOnEsc = true,
    defaultValues,
    schema,
    ...rest
  } = props;

  const styles = useEditableFieldsStyleConfig(props);

  const resolver = useMemo(() => (schema ? zodResolver(schema) : undefined), [schema]);

  const methods = useForm<TValues>({
    defaultValues,
    resolver,
    mode: `all`,
  });
  const {handleSubmit, reset} = methods;

  return (
    <FormProvider {...methods}>
      <Editable
        as={EditableRoot}
        onCancel={() => {
          reset(defaultValues);
        }}
        closeOnBlur={closeOnBlur}
        closeOnEsc={closeOnEsc}
        closeOnClickOutside={closeOnClickOutside}
        onSubmit={() => {
          if (onSubmit) {
            handleSubmit(onSubmit)();
          }
        }}
        __css={styles.root}
        {...rest}
      />
    </FormProvider>
  );
};
